import React, { useState, useEffect } from "react";
import studentAxios from "../constants/studentAxios";

const Timetable = () => {
  const studentId = localStorage.getItem("studentId");
  const sessionId = localStorage.getItem("currentSessionId");

  const [student, setStudent] = useState(null);
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(true);

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Fetch student
  useEffect(() => {
    if (!studentId) return;
    const fetchStudent = async () => {
      try {
        const res = await studentAxios.get(`/students/getStudents/${studentId}`);
        setStudent(res.data);
      } catch (err) {
        console.error("Error fetching student:", err);
      }
    };
    fetchStudent();
  }, [studentId]);

  // Fetch timetable
  useEffect(() => {
    if (!student || !sessionId) return;
    const fetchTimetable = async () => {
      try {
        const res = await studentAxios.get(
          `/timetable/getTimetablesByClassAndSection/${encodeURIComponent(student.class)}/${encodeURIComponent(student.section)}`,
          { params: { sessionId } }
        );
        setTimetable(res.data[0] || null);
      } catch (err) {
        console.error("Error fetching timetable:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTimetable();
  }, [student, sessionId]);

  if (loading) return <div className="flex justify-center items-center h-screen text-lg font-semibold">Loading…</div>;
  if (!timetable) return <div className="flex justify-center items-center h-screen text-lg font-semibold">No timetable found.</div>;

  // Generate time slots
  const addMinutes = (time, mins) => {
    const [h, m] = time.split(":").map(Number);
    const d = new Date();
    d.setHours(h);
    d.setMinutes(m + mins);
    return d.toTimeString().slice(0, 5);
  };

  const calculateTimeSlots = () => {
    const slots = [];
    let current = "08:00";

    if (timetable.assemblyTime) {
      const assemblyEnd = addMinutes(timetable.assemblyTime, timetable.assemblyDuration);
      slots.push({ name: "Assembly", startTime: timetable.assemblyTime, endTime: assemblyEnd });
      current = assemblyEnd;
    }

    for (let i = 0; i < timetable.periodNumber; i++) {
      if (i === timetable.lunchBreakAfterPeriod) {
        const lunchStart = current;
        const lunchEnd = addMinutes(lunchStart, timetable.lunchBreakDuration);
        slots.push({ name: "Lunch Break", startTime: lunchStart, endTime: lunchEnd });
        current = lunchEnd;
      }

      const periodEnd = addMinutes(current, timetable.periodDuration);
      slots.push({ name: `Period ${i + 1}`, startTime: current, endTime: periodEnd });
      current = periodEnd;
    }

    return slots;
  };

  const timeSlots = calculateTimeSlots();

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h1 className="text-3xl font-bold text-center text-blue-600 mb-6">
        Timetable – Class {student.class} ({student.section})
      </h1>

      <div className="overflow-x-auto">
        <table className="table-auto w-full border border-gray-300 rounded-lg shadow-sm">
          <thead className="bg-blue-100">
            <tr>
              <th className="border px-4 py-2">Time / Period</th>
              {daysOfWeek.map((day) => (
                <th key={day} className="border px-4 py-2">{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((slot, idx) => (
              <tr key={idx} className={slot.name.includes("Break") || slot.name === "Assembly" ? "bg-gray-200 font-semibold" : ""}>
                <td className="border px-4 py-2 text-center">
                  {slot.startTime} - {slot.endTime} <br />
                  {slot.name}
                </td>
                {daysOfWeek.map((day) => {
                  const periodData = slot.name.includes("Period")
                    ? timetable.timetable[day][parseInt(slot.name.split(" ")[1]) - 1] || {}
                    : { subject: slot.name };
                  return (
                    <td key={day} className="border px-4 py-2 text-center">
                      {periodData.subject || "--"}<br />
                      {periodData.teacher && <span className="text-gray-600 text-sm">{periodData.teacher}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Timetable;
